/**
 * /well-servicing/rigs/new — add a rig (POST /well-servicing/rigs,
 * fieldiq-api#1269). A full page, not a modal. On save the new rig's own page
 * replaces this one, so Back lands on the register.
 *
 * WS_ADMIN only (admin, office_staff). Any other role is told so here rather
 * than handed a form the API would refuse. A 409 (the unit is already a rig,
 * or the rig number is taken) shows the API's sentence and links to that rig.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWsRig, useWsEquipmentOptions, useWsMeta, useWsRigs } from '../../hooks/useWellServicing';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminWs, duplicateRig, wsRefusal } from '../../lib/well-servicing';
import { useBackToList } from '../downhole/recordPage';
import { RigForm } from './RigForm';
import { WS_EYEBROW, WsEmpty, WsLoading, WsPanel } from './WsBits';

export default function WsNewRigScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/well-servicing/rigs');
  const { user } = useAuth();
  const canAdd = canAdminWs(user?.role);
  const meta = useWsMeta();
  const rigs = useWsRigs();
  const equipment = useWsEquipmentOptions(canAdd);
  const create = useCreateWsRig();
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const failing = [meta, rigs, equipment].find((q) => queryLoadFailure(q));

  return (
    <div data-testid="ws-new-rig-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WS_EYEBROW} title="New rig" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WsPanel eyebrow="Rig register" title="Rig">
            {!canAdd ? (
              <WsEmpty>Only an admin or office staff can add a rig.</WsEmpty>
            ) : meta.isLoading || rigs.isLoading || equipment.isLoading ? (
              <WsLoading label="Loading form" />
            ) : failing ? (
              <LoadFailureNotice
                error={queryFailureError(failing)}
                kind={queryLoadFailure(failing) || 'unreachable'}
                subject={failing === equipment ? 'your equipment' : 'the rig form'}
              />
            ) : (equipment.data ?? []).length === 0 ? (
              <WsEmpty>A rig is a unit in Equipment, and there is no equipment yet. Add the rig's unit under Equipment first.</WsEmpty>
            ) : (
              <RigForm
                rig={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                equipment={equipment.data ?? []}
                rigs={rigs.data?.data ?? []}
                pending={create.isPending}
                error={error}
                duplicateOf={duplicateOf}
                submitLabel="Create rig"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  setDuplicateOf(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Rig created');
                      if (res.data?.id) navigate(`/well-servicing/rigs/${res.data.id}`, { replace: true });
                      else navigate('/well-servicing/rigs', { replace: true });
                    },
                    onError: (err) => {
                      const dup = duplicateRig(err);
                      setError(dup ? dup.message : wsRefusal(err, 'The rig could not be created.'));
                      setDuplicateOf(dup?.existingRigId ?? null);
                    },
                  });
                }}
              />
            )}
          </WsPanel>
        </div>
      </section>
    </div>
  );
}
